/**
 * The pipeline board's columns and cards. CRM-BUILD items 28 and 29.
 *
 * ┌─ ⛔ BUILT AGAINST A CAPABILITY LIST TAKEN FROM THE WIREFRAME FIRST ─────────┐
 * │ §3.11. The contacts list took two inspection rounds and the first found      │
 * │ EIGHT things needing BUILDING - a surface handed over incomplete rather      │
 * │ than imperfect. **The corpus rule names STATES and an absent feature has no  │
 * │ state to photograph**, so nothing systematic reached them.                   │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THE CARD IS DRAWN BY WALKING `board.cardLayout`, NOT BY A FIXED JSX ORDER.
 * Item 29: *"slot-based with no hardcoded slots"*. A slot turned off in the
 * editor disappears here without this file changing, and a slot moved changes
 * where it renders - which is what makes the editor a control rather than a
 * settings page that writes rows nothing reads.
 *
 * ⚠️ SPACING, COLOUR AND TYPOGRAPHY ARE VISUAL-DESIGN'S, after wave 5.
 */
import { useMemo, useState } from 'react'
import { type Board, type BoardCard } from '../data/bookings-api.js'
import { formatMoney } from '../data/money.js'
import { ConfirmDialog } from '../ConfirmDialog.js'
import { CardLayoutEditor } from './CardLayoutEditor.js'
import { useCardMove, type CarriedCard } from './useCardMove.js'

const COUNT = new Intl.NumberFormat()

type Props = {
  board: Board | null
  onOpenBooking: (reference: number) => void
  /** Called after a move changed something, so the section refetches. */
  onChanged: () => void
  onSaved: () => void
}

export function BookingBoard({ board, onOpenBooking, onChanged, onSaved }: Props) {
  const [editing, setEditing] = useState(false)

  const stageOrder = useMemo(() => (board?.columns ?? []).map((c) => c.stageId), [board])
  const stageLabels = useMemo(
    () => new Map((board?.columns ?? []).map((c) => [c.stageId, c.label])),
    [board],
  )
  const move = useCardMove({
    stageOrder,
    stageLabel: (id) => stageLabels.get(id) ?? 'that stage',
    onChanged,
  })

  if (board === null) return null

  /**
   * ⛔ NO PIPELINE IS ITS OWN SENTENCE, not an empty board. A fresh deployment
   * has none, and a board with no columns and nothing saying why is the
   * configured-to-nothing defect arriving on a new surface.
   */
  if (board.emptiness === 'no-pipelines') {
    return (
      <p className="board__state" data-testid="board-empty">
        No pipelines are configured yet, so there is no board to show. A pipeline needs at least one
        active stage, one won stage and one lost stage.
      </p>
    )
  }

  const visibleSlots = board.cardLayout.filter((s) => s.visible)

  return (
    <>
      <div className="board__summary">
        {/** ⛔ THE BOARD'S TOTAL, per R-TOTAL: every result set states its total.
         *  The wireframe draws it as "15 bookings · €4,280". */}
        <p className="board__total" data-testid="board-total">
          {COUNT.format(board.total)} {board.total === 1 ? board.entityLabel : board.entityPlural}
          {' · '}
          {formatMoney(board.valueTotal, board.currency, board.decimalPlaces)}
        </p>
        {/** ⛔ ITEM 30's EDITOR, reached from the surface it configures rather
         *  than from a settings screen: the thing being changed is on screen. */}
        <button
          type="button"
          className="board__configure"
          data-testid="board-configure-cards"
          onClick={() => setEditing(true)}
        >
          Configure Cards
        </button>
      </div>

      {board.emptiness !== 'none' && (
        <p className="board__state" data-testid="board-empty">
          {board.emptiness === 'no-bookings-at-all'
            ? `No ${board.entityPlural.toLowerCase()} yet. They appear here once somebody creates one.`
            : `No ${board.entityPlural.toLowerCase()} match this view.`}
        </p>
      )}

      {/**
       * ⛔ WHAT A SCREEN READER IS TOLD WHILE A CARD IS BEING CARRIED. Pick-up,
       * each column crossed, the drop, and every refusal. **A card being carried
       * is a state with no visual equivalent for somebody who cannot see the
       * board**, so without this the keyboard path is silent as well as invisible.
       */}
      <p className="board__announce" role="status" aria-live="polite" data-testid="board-announce">
        {move.announcement}
      </p>

      <div className="board__columns" data-testid="board-columns">
        {board.columns.map((col) => {
          const carriedHere = move.carried !== null && move.carried.overStageId === col.stageId
          /** ⚠️ The optimistic card is drawn in its TARGET column and removed
           *  from its source, so a drop looks done before the server answers. */
          const cards = col.cards.filter(
            (c) => move.optimistic === null || move.optimistic.bookingId !== c.id,
          )
          const incoming =
            move.optimistic !== null && move.optimistic.stageId === col.stageId
              ? board.columns.flatMap((c) => c.cards).find((c) => c.id === move.optimistic?.bookingId)
              : undefined

          return (
            <section
              key={col.stageId}
              className={carriedHere ? 'board__column board__column--over' : 'board__column'}
              data-stage={col.stageId}
              data-testid="board-column"
              onDragOver={(e) => {
                /** ⛔ `preventDefault` IS WHAT MAKES A DROP TARGET A DROP TARGET.
                 *  Without it the browser refuses the drop and the drag silently
                 *  does nothing, which is a defect with no error anywhere. */
                e.preventDefault()
                move.dragOver(col.stageId)
              }}
              onDrop={(e) => {
                e.preventDefault()
                const raw = e.dataTransfer.getData('application/x-booking')
                if (raw === '') return
                move.dropOn(JSON.parse(raw) as CarriedCard, col.stageId)
              }}
            >
              <header className="board__column-head">
                <h2 className="board__column-name">{col.label}</h2>
                <span className="board__column-meta">
                  {COUNT.format(col.total)} ·{' '}
                  {formatMoney(col.valueTotal, board.currency, board.decimalPlaces)}
                </span>
              </header>

              {cards.length === 0 && incoming === undefined ? (
                /**
                 * ⛔ AN EMPTY COLUMN SAYS SO, IN THE WIREFRAME'S OWN TWO LINES:
                 * *"Nothing here yet / Drag a booking in."* The second line was
                 * missing until the strip above it was read properly, and it is
                 * the line that says the column is a TARGET rather than just bare.
                 */
                <p className="board__column-empty" data-testid="board-column-empty">
                  Nothing here yet.
                  <span className="board__column-empty-hint">Drag a booking in.</span>
                </p>
              ) : (
                <ul className="board__cards">
                  {incoming !== undefined && (
                    <li key={incoming.id}>
                      <Card
                        card={incoming}
                        board={board}
                        slots={visibleSlots}
                        move={move}
                        stageId={col.stageId}
                        onOpen={onOpenBooking}
                        pending
                      />
                    </li>
                  )}
                  {cards.map((card) => (
                    <li key={card.id}>
                      <Card
                        card={card}
                        board={board}
                        slots={visibleSlots}
                        move={move}
                        stageId={col.stageId}
                        onOpen={onOpenBooking}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {/** ⚠️ A CAPPED COLUMN SAYS SO rather than silently showing fewer.
               *  The cap is why the count and the card list can disagree. */}
              {col.total > col.cards.length && (
                <p className="board__column-more" data-testid="board-column-more">
                  Showing {COUNT.format(col.cards.length)} of {COUNT.format(col.total)}.
                </p>
              )}
            </section>
          )
        })}
      </div>

      {/**
       * ⛔ THE ONE REFUSAL, AS A QUESTION RATHER THAN AN ERROR. 0026 refuses to
       * clear a loss reason that nobody asked to clear: *dropping the reason
       * without being asked destroys the only record of why.* So the person is
       * told what is about to be discarded, by name.
       *
       * ⚠️ IT IS `ConfirmDialog`, BUILT FOR AMIR'S FINDING 7 ON THE CONTACTS
       * LIST. Focus lands on Cancel; Escape and click-away are Cancel.
       */}
      {move.confirm !== null && (
        <ConfirmDialog
          title="Reopen This Booking?"
          body={
            move.confirm.lossReason === null
              ? `${move.confirm.title} is recorded as lost. Moving it out of that stage clears that.`
              : `${move.confirm.title} was lost for "${move.confirm.lossReason}". Moving it out of that stage discards that reason, and it is the only record of why.`
          }
          confirmLabel="Reopen and Discard the Reason"
          cancelLabel="Leave It Lost"
          onConfirm={() => move.resolveConfirm(true)}
          onCancel={() => move.resolveConfirm(false)}
        />
      )}

      {editing && (
        <CardLayoutEditor
          layout={board.cardLayout}
          pipelineId={board.pipelineId}
          pipelineLabel={board.pipelineLabel}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            onSaved()
          }}
        />
      )}
    </>
  )
}

/**
 * ⛔ A CARD OPENS THE THREAD AND WRITES NOTHING. Amir's locked decision of
 * 2026-09-11: clicking a booking anywhere opens `/inbox/bookings/<n>`, the
 * thread, with that booking in the panel. **There is no booking page.**
 *
 * ⛔ AND THE GRAB HANDLE IS A BUTTON, SEPARATE FROM THE TITLE. One element that
 * both opens a thread and starts a drag has to guess which the person meant, and
 * a keyboard user pressing Enter on it can only get one of the two. Two controls
 * is the only shape where both verbs are reachable without a pointer.
 */
function Card({
  card,
  board,
  slots,
  move,
  stageId,
  onOpen,
  pending = false,
}: {
  card: BoardCard
  board: Board
  slots: Board['cardLayout']
  move: ReturnType<typeof useCardMove>
  stageId: string
  onOpen: (reference: number) => void
  pending?: boolean
}) {
  const carried = move.carried?.bookingId === card.id
  const asCarried: CarriedCard = {
    bookingId: card.id,
    reference: card.reference,
    title: card.title,
    fromStageId: stageId,
    overStageId: move.carried?.overStageId ?? stageId,
  }

  return (
    <article
      className={
        carried
          ? 'board__card board__card--carried'
          : pending
            ? 'board__card board__card--pending'
            : 'board__card'
      }
      data-testid="board-card"
      data-booking-reference={card.reference}
      data-booking-id={card.id}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-booking', JSON.stringify(asCarried))
        e.dataTransfer.effectAllowed = 'move'
        move.pickUp(asCarried)
      }}
      onDragEnd={() => move.cancel()}
    >
      {/**
       * ⛔ THE KEYBOARD PATH: Enter or Space picks up, Left and Right carry,
       * Enter or Space drops, Escape gives up. Same operation as the drag, same
       * `commit`, so the loss-reason question cannot apply to one and not the
       * other.
       */}
      <button
        type="button"
        className="board__card-grab"
        data-testid="board-card-grab"
        aria-pressed={carried}
        aria-label={
          carried
            ? `Carrying ${card.title}. Left and right arrows choose a stage, Enter drops, Escape cancels.`
            : `Move ${card.title}. Press Enter to pick it up.`
        }
        onClick={() => (carried ? move.drop() : move.pickUp(asCarried))}
        onKeyDown={(e) => {
          if (!carried) return
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            move.carryTo('left')
          } else if (e.key === 'ArrowRight') {
            e.preventDefault()
            move.carryTo('right')
          } else if (e.key === 'Escape') {
            e.preventDefault()
            move.cancel()
          }
        }}
      >
        <span aria-hidden="true">⠿</span>
      </button>

      {/**
       * ⛔ EVERY SLOT IS DRAWN BY WALKING THE LAYOUT. Item 29's "no hardcoded
       * slots" on the rendering side: the order and the on/off come from the
       * server, and adding a slot to the vocabulary does not change this file's
       * structure - only the switch below gains an arm.
       */}
      {slots.map((slot) => (
        <Slot key={slot.key} slot={slot} card={card} board={board} onOpen={onOpen} />
      ))}
    </article>
  )
}

function Slot({
  slot,
  card,
  board,
  onOpen,
}: {
  slot: Board['cardLayout'][number]
  card: BoardCard
  board: Board
  onOpen: (reference: number) => void
}) {
  switch (slot.key) {
    case 'title':
      return (
        <button type="button" className="board__card-open" onClick={() => onOpen(card.reference)}>
          {/** ⛔ Already resolved by the server: the title, else the entity label
           *  and the reference. Amir's ruling A, applied once rather than here. */}
          <span className="board__card-title">{card.title}</span>
        </button>
      )
    case 'contact':
      return (
        <p className="board__card-contact">
          {card.contactName ?? <span className="board__absent">No name</span>}
        </p>
      )
    case 'date':
      return (
        <p className="board__card-date">
          {/** ⛔ A BOOKING WITH NO DATE SAYS SO. §4.17: the date set identifies a
           *  booking, and a booking without one is a record you cannot act on. */}
          {card.dateValue === null ? (
            <span className="board__absent">No date set</span>
          ) : (
            <span>
              {card.dateLabel}: {card.dateValue}
            </span>
          )}
        </p>
      )
    case 'value':
      return (
        <p className="board__card-value">
          {/** ⛔ NULL IS "no value yet" AND IS NOT ZERO. 121 in the corpus by
           *  construction, and a confident 0 is a different claim. */}
          {card.value === null ? (
            <span className="board__absent">No {board.valueLabel.toLowerCase()} yet</span>
          ) : (
            formatMoney(card.value, board.currency, board.decimalPlaces)
          )}
        </p>
      )
    case 'task':
      /**
       * ⛔ THE TASK INDICATOR IS THREE FACTS, NOT ONE. No task at all is a
       * booking nobody has scheduled work on; a task due today is work that is
       * late this evening; a count is neither.
       */
      return (
        <p className="board__card-task">
          {card.openTasks === 0 ? (
            <span className="board__tag board__tag--none">No task</span>
          ) : card.taskDueToday ? (
            <span className="board__tag board__tag--due">Task today</span>
          ) : (
            <span className="board__tag">
              {card.openTasks} {card.openTasks === 1 ? 'task' : 'tasks'}
            </span>
          )}
        </p>
      )
    case 'avatar':
      /** ⚠️ INITIALS RATHER THAN AN IMAGE, because a user has no picture in this
       *  product yet. The unassigned case is stated rather than blank. */
      return (
        <p className="board__card-avatar" title={card.assignedName ?? undefined}>
          {card.assignedInitials === null ? (
            <span className="board__absent">Unassigned</span>
          ) : (
            <span className="board__avatar" aria-label={card.assignedName ?? undefined}>
              {card.assignedInitials}
            </span>
          )}
        </p>
      )
    case 'lastMessage':
      /**
       * ⛔ THE LAST MESSAGE OF THE CONVERSATION WHOSE ACTIVE BOOKING IS THIS ONE.
       * ⚠️ Empty until somebody presses "Make active" on a thread, and the slot
       * says which rather than rendering a blank line: "no message" and "this
       * booking is not the active one anywhere" are different facts.
       */
      return (
        <p className="board__card-last">
          {card.lastMessage === null ? (
            <span className="board__absent">Not the active booking on any thread</span>
          ) : (
            card.lastMessage
          )}
        </p>
      )
    case 'tags':
      return card.tags.length === 0 ? null : (
        <p className="board__card-tags">
          {card.tags.map((t) => (
            <span key={t} className="board__tag board__tag--label">
              {t}
            </span>
          ))}
        </p>
      )
    default:
      /** ⚠️ A SLOT THE SERVER KNOWS AND THIS BUILD DOES NOT renders nothing
       *  rather than throwing. The two halves are deployed together, so this is
       *  a rollback window rather than an ordinary state. */
      return null
  }
}
